import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  Computer as ComputerIcon,
  Logout as LogoutIcon,
  Login as LoginIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';
import { useAuth } from '../context/AuthContext';

export default function LoginHistory() {
  const { user } = useAuth();
  const [loginHistory, setLoginHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const isSuperAdmin = user?.role === 'superadmin';
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isSuperAdmin || isAdmin) {
      loadUsers();
      loadLoginHistory();
    }
  }, [isSuperAdmin, isAdmin]);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setUsers(data.users || []);
      }
    } catch (e) {
      console.error('Failed to load users:', e);
    }
  };

  const loadLoginHistory = async (userId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_BASE_URL}/api/login-history/all?limit=200`;
      if (userId) {
        url += `&userId=${userId}`;
      }
      console.log('Loading login history from:', url);
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      
      console.log('Login history API response:', data);
      
      if (res.ok) {
        const history = data.history || [];
        console.log(`Loaded ${history.length} login history records`);
        setLoginHistory(history);
      } else {
        console.error('Failed to load login history:', data.error);
      }
    } catch (e) {
      console.error('Failed to load login history:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUserFilter = (userId) => {
    setSelectedUserId(userId);
    loadLoginHistory(userId || null);
  };

  const handleRefresh = () => {
    loadLoginHistory(selectedUserId || null);
  };

  const formatTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getDuration = (loginAt, logoutAt) => {
    if (!logoutAt) return 'Active';
    const login = new Date(loginAt);
    const logout = new Date(logoutAt);
    const diff = Math.floor((logout - login) / 1000); // seconds
    const hours = Math.floor(diff / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const filteredHistory = loginHistory.filter((entry) => {
    if (!searchQuery) return true;
    const search = searchQuery.toLowerCase();
    return (
      (entry.full_name || '').toLowerCase().includes(search) ||
      (entry.email || '').toLowerCase().includes(search) ||
      (entry.ip_address || '').toLowerCase().includes(search) ||
      (entry.city || '').toLowerCase().includes(search) ||
      (entry.country || '').toLowerCase().includes(search) ||
      (entry.user_agent || '').toLowerCase().includes(search)
    );
  });

  if (!isSuperAdmin && !isAdmin) {
    return (
      <Layout title="Login History">
        <Box sx={{ p: 3 }}>
          <Typography color="error">Access denied. Admin privileges required.</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Login History">
      <Box sx={{ p: 3 }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Stack spacing={3}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationIcon color="primary" />
                All Users Login History
              </Typography>
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Box>

            {/* Filters */}
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Filter by User</InputLabel>
                <Select
                  value={selectedUserId}
                  onChange={(e) => handleUserFilter(e.target.value)}
                  label="Filter by User"
                >
                  <MenuItem value="">All Users</MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.full_name || u.email} ({u.email})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                fullWidth
                placeholder="Search by name, email, IP, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
              />
            </Stack>

            {/* Table */}
            {loading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">Loading login history...</Typography>
              </Box>
            ) : filteredHistory.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No login history found.</Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Login Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Logout Time</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>IP Address</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Location</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Device/Browser</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredHistory.map((entry) => (
                      <TableRow key={entry.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {entry.full_name || entry.email || 'Unknown User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.email}
                            </Typography>
                            {entry.application_code && (
                              <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                ({entry.application_code})
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LoginIcon fontSize="small" color="success" />
                            <Typography variant="body2">{formatTime(entry.login_at)}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {entry.logout_at ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LogoutIcon fontSize="small" color="error" />
                              <Typography variant="body2">{formatTime(entry.logout_at)}</Typography>
                            </Box>
                          ) : (
                            <Chip label="Active" size="small" color="success" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getDuration(entry.login_at, entry.logout_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={entry.ip_address || 'Unknown'}
                            size="small"
                            icon={<ComputerIcon />}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon fontSize="small" color="action" />
                            <Typography variant="body2">
                              {entry.city && entry.country
                                ? `${entry.city}, ${entry.country}`
                                : entry.location || entry.country || 'Unknown'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                            title={entry.user_agent || 'Unknown'}
                          >
                            {entry.user_agent || 'Unknown'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Summary */}
            {filteredHistory.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                Showing {filteredHistory.length} login record(s)
                {selectedUserId && ` for selected user`}
              </Typography>
            )}
          </Stack>
        </Paper>
      </Box>
    </Layout>
  );
}
